import React, { useEffect, useState } from 'react';
import { BlogcastSeo } from '../components/BlogcastSeo';
import { Link } from 'react-router-dom';
import { ChevronRight, Headphones } from 'lucide-react';
import { SpotifyIcon } from '../components/SpotifyIcon';

const API = process.env.REACT_APP_BACKEND_URL;

// Square poster: symbol artwork + cream brand band in the standard site font.
const CoverPoster = ({ show }) => (
  <div className="relative aspect-square overflow-hidden bg-[#141210]">
    {show.spotify_url && (
      <button
        type="button"
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); window.open(show.spotify_url, '_blank', 'noopener,noreferrer'); }}
        aria-label={`Listen to ${show.title} on Spotify`}
        data-testid={`blogcast-spotify-${show.slug}`}
        className="absolute top-3 right-3 z-10 inline-flex items-center gap-1.5 rounded-full bg-[#1DB954] text-black text-xs font-semibold px-3 py-1.5 shadow-md hover:bg-[#1ed760] transition-colors"
      >
        <SpotifyIcon className="w-3.5 h-3.5" /> Spotify
      </button>
    )}
    {show.cover ? (
      <img
        src={show.cover}
        alt={show.title}
        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.04]"
      />
    ) : (
      <div className="w-full h-full flex items-center justify-center">
        <Headphones className="h-10 w-10 text-[#F5F5DC]/25" />
      </div>
    )}
    <div className="absolute inset-x-0 bottom-0 bg-[#F5F5DC] px-4 py-3 text-center">
      <p className="text-[10px] tracking-[0.3em] text-black/55 uppercase">BWGELA</p>
      <h3 className="text-black font-bold uppercase leading-tight tracking-tight text-sm sm:text-base line-clamp-2 mt-0.5">
        {show.title}
      </h3>
      <p className="text-[11px] italic text-black/55 mt-0.5">The Blogcast</p>
    </div>
  </div>
);

const Blogcast = () => {
  const [shows, setShows] = useState([]);

  useEffect(() => {
    window.scrollTo(0, 0);
    fetch(`${API}/api/blogcast/shows`)
      .then((r) => r.json())
      .then((d) => setShows(d.shows || []))
      .catch(() => setShows([]));
  }, []);

  return (
    <div className="bg-[#0a0a0a] pt-[120px] sm:pt-[140px] lg:pt-[160px] pb-[120px] px-4 sm:px-6 lg:px-8" data-testid="blogcast-page">
      <BlogcastSeo />
      <div className="max-w-5xl mx-auto">
        <div className="text-center mb-14 sm:mb-20">
          <h1 className="text-3xl md:text-4xl text-[#F5F5DC] mb-4">The BWGELA Blogcast</h1>
          <p className="text-base text-white/80 max-w-2xl mx-auto leading-relaxed">
            Listen, download and share here on the BWGELA Player or your Spotify App.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-7">
          {shows.map((show) => {
            const live = show.episode_count > 0;
            const inner = (
              <>
                <CoverPoster show={show} />
                <div className="p-5">
                  <p className="text-gray-400 text-sm mb-4 leading-relaxed line-clamp-2">{show.description}</p>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-500">
                      {live ? `${show.episode_count} episode${show.episode_count > 1 ? 's' : ''}` : 'Coming soon'}
                    </span>
                    {live && (
                      <span className="text-[#F5F5DC] flex items-center gap-1 group-hover:gap-2 transition-all">
                        Listen <ChevronRight className="h-4 w-4" />
                      </span>
                    )}
                  </div>
                </div>
              </>
            );
            const classes = `group bg-[#1a1a1a] rounded-2xl overflow-hidden border border-[#2a2a2a] focus:outline-none focus-visible:outline-none transition-all duration-300 ${live ? 'hover:border-[#444] hover:-translate-y-1 cursor-pointer' : 'opacity-60'}`;
            return live ? (
              <Link key={show.slug} to={`/blogcast/${show.slug}`} data-testid={`blogcast-show-${show.slug}`} className={classes}>
                {inner}
              </Link>
            ) : (
              <div key={show.slug} data-testid={`blogcast-show-${show.slug}`} className={classes}>
                {inner}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default Blogcast;
